<template>
  <section id="about" class="ec-section about">
    <div class="ec-container about__inner">
      <div class="about__copy">
        <span class="ec-eyebrow" v-reveal>{{ $t('about.eyebrow') }}</span>
        <h2 class="ec-title" v-reveal="60">{{ $t('about.titleLine1') }}<br /><span class="ec-gradient-text">{{ $t('about.titleHi') }}</span></h2>
        <p class="about__text" v-reveal="120">{{ $t('about.text1') }}</p>
        <p class="about__text" v-reveal="160">{{ $t('about.text2') }}</p>

        <div class="about__pillars" v-reveal="200">
          <div v-for="p in pillars" :key="p.title" class="pillar">
            <span class="pillar__icon">{{ p.icon }}</span>
            <strong>{{ p.title }}</strong>
            <span class="pillar__desc">{{ p.desc }}</span>
          </div>
        </div>

        <div class="about__certs" v-reveal="240">
          <span class="about__certs-label">{{ $t('about.certsLabel') }}</span>
          <div class="about__certs-list">
            <span v-for="c in certs" :key="c">{{ c }}</span>
          </div>
        </div>
      </div>

      <div class="about__visual" v-reveal="120">
        <div class="orbit">
          <div class="orbit__core">
            <strong>Easycloud</strong>
            <em>{{ $t('about.orbitSub') }}</em>
          </div>
          <div class="orbit__ring orbit__ring--1">
            <span class="node" v-for="n in ring1" :key="n">{{ n }}</span>
          </div>
          <div class="orbit__ring orbit__ring--2">
            <span class="node" v-for="n in ring2" :key="n">{{ n }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'AboutSection',
  data() {
    return {
      ring1: ['☁️', '🛡️', '⚡', '📊'],
      ring2: ['🤖', '🌐', '🖧️', '⚙️', '📦', '🚀']
    }
  },
  computed: {
    pillars() { return this.$tm('about.pillars') },
    certs() { return this.$tm('about.certs') }
  }
}
</script>

<style scoped>
.about__inner { display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 60px; align-items: center; }

.about__text { color: var(--ec-text-soft); font-size: 16px; margin-bottom: 18px; max-width: 560px; }

.about__pillars { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 30px 0; }
.pillar { padding: 20px 18px; background: var(--ec-card); border: 1px solid var(--ec-line); border-radius: 16px; }
.pillar__icon { font-size: 24px; display: block; margin-bottom: 10px; }
.pillar strong { font-size: 16px; display: block; margin-bottom: 4px; }
.pillar__desc { font-size: 13px; color: var(--ec-text-mute); }

.about__certs { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.about__certs-label { font-size: 13px; color: var(--ec-text-mute); font-weight: 600; }
.about__certs-list { display: flex; gap: 10px; flex-wrap: wrap; }
.about__certs-list span { font-size: 13px; padding: 6px 14px; border-radius: 999px; background: var(--ec-grad-soft); border: 1px solid rgba(255,153,0,.25); color: var(--ec-text); }

/* Orbit visual */
.about__visual { display: grid; place-items: center; }
.orbit { position: relative; width: 360px; height: 360px; display: grid; place-items: center; }
.orbit__core {
  position: relative; z-index: 5; width: 140px; height: 140px; border-radius: 50%;
  display: grid; place-items: center; text-align: center;
  background: var(--ec-grad); color: #04101f;
  box-shadow: 0 0 60px -8px rgba(255,153,0,.7);
}
.orbit__core strong { font-size: 18px; display: block; }
.orbit__core em { font-style: normal; font-size: 11px; letter-spacing: .15em; }
.orbit__ring { position: absolute; border-radius: 50%; border: 1px dashed rgba(255,255,255,.16); }
.orbit__ring--1 { width: 240px; height: 240px; animation: rot 22s linear infinite; }
.orbit__ring--2 { width: 360px; height: 360px; animation: rot 36s linear infinite reverse; }
.node {
  position: absolute; width: 44px; height: 44px; border-radius: 50%;
  display: grid; place-items: center; font-size: 20px;
  background: rgba(13,19,40,.95); border: 1px solid var(--ec-line);
  box-shadow: 0 8px 22px -8px rgba(0,0,0,.6);
}
/* distribute ring1 nodes (4) */
.orbit__ring--1 .node:nth-child(1) { top: -22px; left: 50%; margin-left: -22px; }
.orbit__ring--1 .node:nth-child(2) { right: -22px; top: 50%; margin-top: -22px; }
.orbit__ring--1 .node:nth-child(3) { bottom: -22px; left: 50%; margin-left: -22px; }
.orbit__ring--1 .node:nth-child(4) { left: -22px; top: 50%; margin-top: -22px; }
/* distribute ring2 nodes (6) */
.orbit__ring--2 .node:nth-child(1) { top: -22px; left: 50%; margin-left: -22px; }
.orbit__ring--2 .node:nth-child(2) { top: 22%; right: -10px; }
.orbit__ring--2 .node:nth-child(3) { bottom: 22%; right: -10px; }
.orbit__ring--2 .node:nth-child(4) { bottom: -22px; left: 50%; margin-left: -22px; }
.orbit__ring--2 .node:nth-child(5) { bottom: 22%; left: -10px; }
.orbit__ring--2 .node:nth-child(6) { top: 22%; left: -10px; }
@keyframes rot { to { transform: rotate(360deg); } }
.orbit__ring .node { animation: counter-rot 22s linear infinite; }
.orbit__ring--2 .node { animation: counter-rot 36s linear infinite; }
@keyframes counter-rot { to { transform: rotate(-360deg); } }

@media (max-width: 900px) {
  .about__inner { grid-template-columns: 1fr; gap: 50px; }
  .about__pillars { grid-template-columns: 1fr; }
}
/* Shrink the orbit's real layout box (not just a visual transform) so it fits phones */
@media (max-width: 560px) {
  .orbit { width: 280px; height: 280px; }
  .orbit__ring--1 { width: 188px; height: 188px; }
  .orbit__ring--2 { width: 280px; height: 280px; }
  .orbit__core { width: 116px; height: 116px; }
}
@media (max-width: 360px) {
  .orbit { width: 248px; height: 248px; }
  .orbit__ring--1 { width: 168px; height: 168px; }
  .orbit__ring--2 { width: 248px; height: 248px; }
}
</style>
